import { useState } from 'react';
import { X, Save, Calendar, Hash, DollarSign, Building, User, FileText, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function InvoiceReviewModal({ invoice, onClose, onSave }) {
  const [formData, setFormData] = useState({
    invoice_number: invoice.extracted_data.invoice_number || '',
    invoice_date: invoice.extracted_data.invoice_date || '',
    due_date: invoice.extracted_data.due_date || '',
    vendor_name: invoice.extracted_data.vendor_name || '',
    vendor_address: invoice.extracted_data.vendor_address || '',
    vendor_email: invoice.extracted_data.vendor_email || '',
    vendor_phone: invoice.extracted_data.vendor_phone || '',
    vendor_tax_id: invoice.extracted_data.vendor_tax_id || '',
    customer_name: invoice.extracted_data.customer_name || '',
    customer_address: invoice.extracted_data.customer_address || '',
    customer_email: invoice.extracted_data.customer_email || '',
    customer_phone: invoice.extracted_data.customer_phone || '',
    subtotal: invoice.extracted_data.subtotal || '',
    gst_amount: invoice.extracted_data.gst_amount || '',
    tax_rate: invoice.extracted_data.tax_rate || '',
    discount: invoice.extracted_data.discount || '',
    shipping: invoice.extracted_data.shipping || '',
    other_charges: invoice.extracted_data.other_charges || '',
    total_amount: invoice.extracted_data.total_amount || '',
    payment_terms: invoice.extracted_data.payment_terms || '',
    payment_method: invoice.extracted_data.payment_method || '',
    currency: invoice.extracted_data.currency || '',
    notes: invoice.extracted_data.notes || '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Convert numeric fields
    const numericFields = ['subtotal', 'gst_amount', 'tax_rate', 'discount', 'shipping', 'other_charges', 'total_amount'];
    const processedData = { ...formData };
    
    numericFields.forEach(field => {
      processedData[field] = processedData[field] ? parseFloat(processedData[field]) : null;
    });
    
    onSave(processedData);
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white sm:rounded-2xl shadow-xl w-full h-full sm:h-auto sm:max-w-5xl sm:max-h-[90vh] overflow-hidden"
          data-testid="invoice-review-modal"
        >
          <div className="border-b border-slate-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between sticky top-0 bg-white z-10">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg sm:text-2xl font-heading font-semibold text-slate-900 truncate">Review Invoice</h2>
              <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate">{invoice.filename}</p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 transition-colors ml-2 p-1"
              data-testid="close-modal-button"
            >
              <X className="w-5 sm:w-6 h-5 sm:h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6 sm:space-y-8 overflow-y-auto max-h-[calc(100vh-120px)] sm:max-h-[calc(90vh-140px)]">
            {/* Invoice Information */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-3 sm:mb-4 flex items-center space-x-2">
                <FileText className="w-4 sm:w-5 h-4 sm:h-5 text-indigo-700" />
                <span>Invoice Information</span>
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">Invoice Number</label>
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) => handleChange('invoice_number', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    placeholder="INV-12345"
                    data-testid="input-invoice-number"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1.5 sm:mb-2">Invoice Date</label>
                  <input
                    type="date"
                    value={formData.invoice_date}
                    onChange={(e) => handleChange('invoice_date', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    data-testid="input-invoice-date"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => handleChange('due_date', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    data-testid="input-due-date"
                  />
                </div>
              </div>
            </div>

            {/* Vendor Information */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                <Building className="w-5 h-5 text-indigo-700" />
                <span>Vendor/From</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Vendor Name</label>
                  <input
                    type="text"
                    value={formData.vendor_name}
                    onChange={(e) => handleChange('vendor_name', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    data-testid="input-vendor-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tax ID/GST</label>
                  <input
                    type="text"
                    value={formData.vendor_tax_id}
                    onChange={(e) => handleChange('vendor_tax_id', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={formData.vendor_address}
                    onChange={(e) => handleChange('vendor_address', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.vendor_email}
                    onChange={(e) => handleChange('vendor_email', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.vendor_phone}
                    onChange={(e) => handleChange('vendor_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                <User className="w-5 h-5 text-indigo-700" />
                <span>Customer/To</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Customer Name</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => handleChange('customer_name', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Address</label>
                  <input
                    type="text"
                    value={formData.customer_address}
                    onChange={(e) => handleChange('customer_address', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => handleChange('customer_email', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => handleChange('customer_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Financial Details */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-emerald-500" />
                <span>Financial Details</span>
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Currency</label>
                  <input
                    type="text"
                    value={formData.currency}
                    onChange={(e) => handleChange('currency', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    placeholder="USD"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Subtotal</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.subtotal}
                    onChange={(e) => handleChange('subtotal', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    data-testid="input-subtotal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tax/GST</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.gst_amount}
                    onChange={(e) => handleChange('gst_amount', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    data-testid="input-gst-amount"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tax_rate}
                    onChange={(e) => handleChange('tax_rate', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Discount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.discount}
                    onChange={(e) => handleChange('discount', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Shipping</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.shipping}
                    onChange={(e) => handleChange('shipping', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Other Charges</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.other_charges}
                    onChange={(e) => handleChange('other_charges', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2 font-semibold">Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.total_amount}
                    onChange={(e) => handleChange('total_amount', e.target.value)}
                    className="w-full px-3 py-2 border border-emerald-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent font-semibold bg-emerald-50"
                    data-testid="input-total-amount"
                  />
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div>
              <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-indigo-700" />
                <span>Payment Information</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Payment Terms</label>
                  <input
                    type="text"
                    value={formData.payment_terms}
                    onChange={(e) => handleChange('payment_terms', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    placeholder="Net 30"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Payment Method</label>
                  <input
                    type="text"
                    value={formData.payment_method}
                    onChange={(e) => handleChange('payment_method', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent"
                    placeholder="Bank Transfer"
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:border-transparent resize-none"
                placeholder="Additional notes or terms..."
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-200 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 font-medium rounded-lg"
                data-testid="cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 bg-indigo-700 text-white hover:bg-indigo-800 shadow-sm hover:shadow-md transition-all duration-200 font-medium rounded-lg flex items-center space-x-2"
                data-testid="save-button"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default InvoiceReviewModal;
